import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Column } from "../misc/Column";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { Form2 } from "../misc/Form2";
import { BooleanInput } from "../inputs/BooleanInput";
import { DateInput2 } from "../inputs/DateInput2";
import type { DialogProps } from "../misc/DialogProps";
import type { DateWithoutTime } from "../model/DateWithoutTime";
import LoadingButton from "@mui/lab/LoadingButton";
import TableViewIcon from "@mui/icons-material/TableView";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useFormContext } from "react-hook-form";
import { useState } from "react";

export type ReportConfiguration = {
  from: DateWithoutTime;
  to: DateWithoutTime;
  includePlannedAppointments: boolean;
};

export type ReportFormat = "Excel" | "PDF";

export interface Report {
  localizedTitle: string;
  create: (args: { config: ReportConfiguration; format: ReportFormat; onSuccess: () => void }) => Promise<void>;
}

export function CreateReportDialog(props: { defaultValues: ReportConfiguration; report: Report } & DialogProps) {
  const { defaultValues, report, ...dialogProps } = props;
  const [creatingPdfFile, setCreatingPdfFile] = useState(false);
  return (
    <Dialog {...dialogProps} maxWidth="sm" fullWidth>
      <DialogTitle>{report.localizedTitle} erstellen</DialogTitle>
      <DialogCloseButton onClick={dialogProps.onClose} />
      <Form2<ReportConfiguration>
        defaultValues={defaultValues}
        onSubmit={(config) => {
          setCreatingPdfFile(true);
          return report.create({ config, format: "PDF", onSuccess: dialogProps.onClose });
        }}
      >
        <DialogContent>
          <_FormFields />
        </DialogContent>
        <DialogActions
          disableSpacing
          sx={{
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
          }}
        >
          <_CreateExcelReportButton report={report} onSuccess={dialogProps.onClose} />
          <_CreatePdfReportSubmitButton loading={creatingPdfFile} />
        </DialogActions>
      </Form2>
    </Dialog>
  );
}

function _FormFields() {
  const { watch } = useFormContext<ReportConfiguration>();
  const from = watch("from");
  return (
    <Column sx={{ gap: 2 }}>
      <DateInput2 source="from" label="von" size="small" allowPastDates />
      <DateInput2
        source="to"
        label="bis"
        size="small"
        allowPastDates
        validate={(value) => {
          if (value && from && value < from) {
            return `Bitte gib ein Datum nach dem ${from.toLocalizedString()} ein.`;
          }
          return true;
        }}
      />
      <BooleanInput
        source="includePlannedAppointments"
        label="geplante Termine berücksichtigen"
        sx={{
          "& .MuiFormControlLabel-root": { alignItems: { xs: "flex-start", sm: "center" }, mr: { xs: 0, sm: 2 } },
          "& .MuiTypography-root": { whiteSpace: { xs: "normal", sm: "nowrap" } },
        }}
      />
    </Column>
  );
}

function _CreateExcelReportButton(props: { report: Report; onSuccess: () => void }) {
  const { report, onSuccess } = props;
  const { handleSubmit } = useFormContext<ReportConfiguration>();
  const [creatingExcelFile, setCreatingExcelFile] = useState(false);
  return (
    <LoadingButton
      variant="outlined"
      loading={creatingExcelFile}
      type="button"
      startIcon={<TableViewIcon />}
      onClick={handleSubmit((config) => {
        setCreatingExcelFile(true);
        return report.create({ config, format: "Excel", onSuccess });
      })}
    >
      Excel-Datei erstellen
    </LoadingButton>
  );
}

function _CreatePdfReportSubmitButton(props: { loading: boolean }) {
  const { loading } = props;
  return (
    <LoadingButton variant="outlined" loading={loading} type="submit" startIcon={<PictureAsPdfIcon />}>
      PDF-Datei erstellen
    </LoadingButton>
  );
}
